import type { ArticleValidator, BlockValidator, ValidationError } from './types'

// ─── Helpers ──────────────────────────────────────────────────────────────────

function blockError(
  blockId: string,
  code: string,
  message: string,
  severity: ValidationError['severity'] = 'error'
): ValidationError {
  return { scope: 'block', blockId, code, severity, message }
}

function fieldError(
  blockId: string,
  field: string,
  code: string,
  message: string,
  severity: ValidationError['severity'] = 'error'
): ValidationError {
  return { scope: 'field', blockId, field, code, severity, message }
}

function articleError(
  code: string,
  message: string,
  severity: ValidationError['severity'] = 'error'
): ValidationError {
  return { scope: 'article', code, severity, message }
}

// ─── Structural block validators ──────────────────────────────────────────────
// These check that required fields are present and non-trivially populated.

export const validateHeading: BlockValidator = (block) => {
  if (block.type !== 'heading') return []
  const errors: ValidationError[] = []
  if (!block.content.trim()) {
    errors.push(fieldError(block.id, 'content', 'heading.empty', 'Heading text is empty.'))
  }
  return errors
}

export const validateParagraph: BlockValidator = (block) => {
  if (block.type !== 'paragraph') return []
  const errors: ValidationError[] = []
  if (block.children.length === 0) {
    errors.push(blockError(block.id, 'paragraph.empty', 'Paragraph has no content.'))
  }
  const text = block.children
    .map(c => (c.type === 'span' ? c.text : c.type === 'link' ? c.text : ''))
    .join('')
  if (block.children.length > 0 && !text.trim()) {
    errors.push(blockError(block.id, 'paragraph.whitespace-only', 'Paragraph contains only whitespace.'))
  }
  return errors
}

export const validateList: BlockValidator = (block) => {
  if (block.type !== 'list') return []
  const errors: ValidationError[] = []
  block.items.forEach((item, i) => {
    const text = item.children
      .map(c => (c.type === 'span' ? c.text : c.type === 'link' ? c.text : ''))
      .join('')
    if (!text.trim()) {
      errors.push(
        fieldError(block.id, `items[${i}]`, 'list.empty-item', `List item ${i + 1} is empty.`, 'warning')
      )
    }
  })
  return errors
}

export const validateSourceList: BlockValidator = (block) => {
  if (block.type !== 'source-list') return []
  const errors: ValidationError[] = []
  block.entries.forEach((entry, i) => {
    if (!entry.text.trim()) {
      errors.push(
        fieldError(block.id, `entries[${i}]`, 'source-list.empty-entry', `Source entry ${i + 1} has no text.`, 'warning')
      )
    }
    if (!entry.id) {
      errors.push(
        fieldError(block.id, `entries[${i}].id`, 'source-list.missing-id', `Source entry ${i + 1} is missing an ID.`)
      )
    }
  })
  return errors
}

export const validateWarning: BlockValidator = (block) => {
  if (block.type !== 'warning') return []
  if (!block.content.trim()) {
    return [blockError(block.id, 'warning.empty', 'Warning block has no content.')]
  }
  return []
}

/**
 * Ensures content blocks carry a non-empty classification/portion mark.
 * Applies to all block types that render inline text (paragraph, heading, list,
 * warning, textbox, table, visual). Structural/metadata blocks are excluded.
 */
const CLASSIFIED_BLOCK_TYPES = new Set([
  'paragraph', 'heading', 'list', 'warning', 'textbox', 'table', 'visual',
])

export const validateBlockClassification: BlockValidator = (block) => {
  if (!CLASSIFIED_BLOCK_TYPES.has(block.type)) return []
  if (!block.classification) {
    return [
      fieldError(
        block.id,
        'classification',
        'block.missing-classification',
        `${block.type.charAt(0).toUpperCase() + block.type.slice(1)} block is missing a portion mark.`
      ),
    ]
  }
  return []
}

// ─── Heuristic block validators ───────────────────────────────────────────────
// These detect probable parse artifacts. All produce 'warning' severity.

/** Detects `)` or `//` at the start of a block's text — likely a classification bleed. */
export const detectClassificationBleed: BlockValidator = (block) => {
  let firstText: string | null = null

  if (block.type === 'paragraph') {
    const first = block.children.find(c => c.type === 'span')
    if (first?.type === 'span') firstText = first.text
  } else if (block.type === 'heading') {
    firstText = block.content
  } else if (block.type === 'warning') {
    firstText = block.content
  } else if (block.type === 'list') {
    const firstItem = block.items[0]
    if (firstItem) {
      const first = firstItem.children.find(c => c.type === 'span')
      if (first?.type === 'span') firstText = first.text
    }
  } else if (block.type === 'source-list') {
    firstText = block.entries[0]?.text ?? null
  } else if (block.type === 'textbox' && block.title) {
    firstText = block.title
  } else if (block.type === 'table') {
    const firstRow = block.rows[0]
    if (firstRow) {
      const firstCell = firstRow.cells[0]
      const first = firstCell?.children.find(c => c.type === 'span')
      if (first?.type === 'span') firstText = first.text
    }
  }

  if (firstText && /^[)\s]*\/\//.test(firstText)) {
    return [
      blockError(
        block.id,
        'heuristic.classification-bleed',
        'Block text starts with "//") — may be a classification fragment left over from parsing.',
        'warning'
      ),
    ]
  }

  if (firstText && /^\s*\)+/.test(firstText)) {
    return [
      blockError(
        block.id,
        'heuristic.lone-closing-paren',
        'Block text starts with a lone ")" — may be a split portion mark.',
        'warning'
      ),
    ]
  }

  return []
}

/** Detects a paragraph that is suspiciously short (single character or punctuation only). */
export const detectTrivialParagraph: BlockValidator = (block) => {
  if (block.type !== 'paragraph') return []
  const text = block.children
    .map(c => (c.type === 'span' ? c.text : c.type === 'link' ? c.text : ''))
    .join('')
    .trim()
  if (text.length > 0 && text.length <= 2) {
    return [
      blockError(
        block.id,
        'heuristic.trivial-paragraph',
        `Paragraph contains only "${text}" — likely a parse artifact.`,
        'warning'
      ),
    ]
  }
  return []
}

// ─── Article-scoped validators ────────────────────────────────────────────────

/** Ensures the article has a non-empty title. */
export const validateArticleTitle: ArticleValidator = (article) => {
  if (!article.title?.trim()) {
    return [articleError('article.missing-title', 'Article title is missing or empty.')]
  }
  return []
}

/** Ensures the article has at least one classification token. */
export const validateArticleClassification: ArticleValidator = (article) => {
  if (!article.classification) {
    return [articleError('article.missing-classification', 'Article has no classification marking.')]
  }
  return []
}

/**
 * Checks that every citation block references a source ID that exists in the
 * article's source-list entries.
 */
export const validateCitationIntegrity: ArticleValidator = (article, ctx) => {
  const errors: ValidationError[] = []

  for (const block of article.blocks) {
    const children =
      block.type === 'paragraph' ? block.children :
      block.type === 'list' ? block.items.flatMap(i => i.children) :
      []

    for (const node of children) {
      if (node.type === 'citation' && !ctx.sourceIds.has(node.sourceId)) {
        errors.push(
          blockError(
            block.id,
            'citation.unresolved',
            `Citation references source ID "${node.sourceId}" which does not exist in the source list.`,
            'error'
          )
        )
      }
    }
  }

  return errors
}

/** Warns about duplicate section markers of the same sectionType. */
export const validateSectionMarkerDuplicates: ArticleValidator = (article) => {
  const seen = new Map<string, number>()
  for (const block of article.blocks) {
    if (block.type === 'section-marker') {
      seen.set(block.sectionType, (seen.get(block.sectionType) ?? 0) + 1)
    }
  }
  const errors: ValidationError[] = []
  for (const [type, count] of seen) {
    if (count > 1) {
      errors.push(
        articleError(
          'article.duplicate-section-marker',
          `Section type "${type}" appears ${count} times. Expected at most once.`,
          'warning'
        )
      )
    }
  }
  return errors
}

// ─── Rule registry ────────────────────────────────────────────────────────────

/** All block-level validators run on every block in order. */
export const BLOCK_VALIDATORS: BlockValidator[] = [
  validateHeading,
  validateParagraph,
  validateList,
  validateSourceList,
  validateWarning,
  validateBlockClassification,
  detectClassificationBleed,
  detectTrivialParagraph,
]

/** All article-level validators. Run after block validators. */
export const ARTICLE_VALIDATORS: ArticleValidator[] = [
  validateArticleTitle,
  validateArticleClassification,
  validateCitationIntegrity,
  validateSectionMarkerDuplicates,
]
